import dayjs from "dayjs";
import { defaultTo } from "es-toolkit/compat";

import { useGlobal } from "@/migrated-widgets/table-map/contexts/GlobalState";
import { useAsync } from "@/migrated-widgets/table-map/hooks/useAsync";
import { MapDataLayerService, PortalV2Service } from "@/migrated-widgets/table-map/services";
import { GetTransactionModelLevelDataResponse, SalesData } from "@/migrated-widgets/table-map/services/MapDataLayer/types/Transactions.types";
import { Environments } from "@/migrated-widgets/table-map/constants/Environments";

// Stable empty array to prevent infinite re-renders
const EMPTY_TRANSACTIONS: SalesData[] = [];

export const useTableMapData = () => {
    const { params, tableMapConfig } = useGlobal();

    const { response: locations, loading: loadingLocations } = useAsync(async () => {
        const product = Number(params.product) || 0;
        const productIds = tableMapConfig?.productIds || [1, 3, 14, 15];
        const _params = {
            // widgetId: params.widgetId || "",
            productName: "",
            level: "LOCATION",
            segment: "campaign",
            startDate: params.startDate,
            endDate: params.endDate,
            dealerIds: params.dealerId,
            userGuid: params.userGuid,
            token: params.token,
            date: new Date().toString(),
        };
        if (product === 0) {
            const promises = await Promise.all(
                productIds.map((productId) =>
                    PortalV2Service.getLocationData({
                        urlPath: `/mdl/getlocations?product=${productId}`,
                        ..._params,
                    }),
                ),
            );

            const result = promises.reduce((acc: any, crr) => {
                return {
                    radius: {
                        type: "FeatureCollection",
                        features: [...defaultTo(acc?.radius?.features, []), ...defaultTo(crr?.radius?.features, [])],
                    },
                    location: {
                        type: "FeatureCollection",
                        features: [...defaultTo(acc?.location?.features, []), ...defaultTo(crr?.location?.features, [])],
                    },
                    states: {
                        type: "FeatureCollection",
                        features: [...defaultTo(acc?.states?.features, []), ...defaultTo(crr?.states?.features, [])],
                    },
                    cities: {
                        type: "FeatureCollection",
                        features: [...defaultTo(acc?.cities?.features, []), ...defaultTo(crr?.cities?.features, [])],
                    },
                    zipRegion: {
                        type: "FeatureCollection",
                        features: [...defaultTo(acc?.zipRegion?.features, []), ...defaultTo(crr?.zipRegion?.features, [])],
                    },
                    circleMarkers: [...defaultTo(acc?.circleMarkers, []), ...defaultTo(crr?.circleMarkers, [])],
                    geometryFeatures: [...defaultTo(acc?.geometryFeatures, []), ...defaultTo(crr?.geometryFeatures, [])],
                };
            }, {});

            return result;
        }

        return await PortalV2Service.getLocationData({
            urlPath: `/mdl/getlocations?product=${product}`,
            ..._params,
        });
    }, [params]);

    const showTransactions = tableMapConfig?.showTransactions !== false;

    const { response: transactions = EMPTY_TRANSACTIONS, loading: loadingTransactions = true } = useAsync(async () => {
        if (!showTransactions) {
            return EMPTY_TRANSACTIONS;
        }
        const res = await MapDataLayerService.get<GetTransactionModelLevelDataResponse>(Environments.TRANSACTIONS_ENDPOINT, {
            level: "model",
            segment: "zipcode",
            startDate: dayjs(params.startDate).format("YYYY/MM/DD"),
            endDate: dayjs(params.endDate ?? new Date()).format("YYYY/MM/DD"),
            dealerId: params.dealerRefId,
            token: params.token,
            transactionTypes: "L,S,R",
        });

        const dataCollection = (res.elementCollection[0]?.dataCollection || []).map((item) => ({
            ...item,
            vehicleType: item.vehicleType ? item.vehicleType.toUpperCase() : item.vehicleType,
        }));

        return dataCollection;
    }, [params, tableMapConfig]);

    return {
        locations,
        loadingLocations,
        transactions,
        loadingTransactions,
    };
};
